// SPDX-License-Identifier: AGPL-3.0-or-later

import * as FeedCommands from '@app/features/feed/commands/FeedCommands';
import styles from '@app/features/feed/components/FeedVideo.module.css';
import {FeedVideoPlayer} from '@app/features/feed/components/FeedVideoPlayer';
import FeedAutoplay from '@app/features/feed/state/FeedAutoplay';
import {Logger} from '@app/features/platform/utils/AppLogger';
import FocusRing from '@app/features/ui/focus_ring/FocusRing';
import {formatDuration} from '@fluxer/date_utils/src/DateDuration';
import type {FeedVideoResponse} from '@fluxer/schema/src/domains/feed/FeedResponseSchemas';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {PlayIcon} from '@phosphor-icons/react';
import {clsx} from 'clsx';
import type Hls from 'hls.js';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useCallback, useEffect, useId, useRef, useState} from 'react';

const logger = new Logger('FeedVideo');

const PLAY_VIDEO_DESCRIPTOR = msg({
	message: 'Play video',
	comment: 'Accessible label of the play button on the poster of a video in a feed post.',
});
const UNPLAYABLE_DESCRIPTOR = msg({
	message: "This video can't be played in this browser.",
	comment:
		'Shown in place of a feed post video when the browser can play neither its stream (HLS) nor an MP4 copy of it.',
});
const PLAYBACK_FAILED_DESCRIPTOR = msg({
	message: "Couldn't play this video.",
	comment: 'Shown in place of a feed post video when playing it stopped with an error (network or broken stream).',
});

const HLS_MIME_TYPE = 'application/vnd.apple.mpegurl';
// The signed URLs work at least 2 h (or the video's length plus 30 min, at most 6 h) after the post was loaded; closer to
// their end than the video's length and this, the post is loaded again (the segments are fetched as it plays).
const URL_RENEW_BEFORE_MS = 10 * 60 * 1000;
// Clips up to a minute start over at their end (X, Instagram); longer videos stop.
const LOOP_MAX_SECONDS = 60;

const urlsRunOut = (video: FeedVideoResponse) =>
	Date.parse(video.expires_at) - Date.now() < URL_RENEW_BEFORE_MS + (video.duration ?? 0) * 1000;

// The URLs are taken when playing starts, so a refetched post (new signed URLs) does not restart a playing video.
type Playback =
	| {kind: 'poster'}
	| {kind: 'starting'}
	| {kind: 'playing'; src: string; Hls: typeof Hls | null}
	| {kind: 'unplayable'}
	| {kind: 'failed'};

// Native HLS (Safari, iOS), else hls.js where Media Source works (loaded here, on the first play, as its own chunk),
// else the MP4 copy when the server has one.
async function choosePlayback(video: FeedVideoResponse): Promise<Playback> {
	if (document.createElement('video').canPlayType(HLS_MIME_TYPE) !== '') {
		return {kind: 'playing', src: video.hls_url, Hls: null};
	}
	try {
		// The light build (no subtitles, alternate audio or DRM): Bunny's HLS has its audio in the video segments.
		const {default: HlsPlayer} = await import('hls.js/light');
		if (HlsPlayer.isSupported()) return {kind: 'playing', src: video.hls_url, Hls: HlsPlayer};
	} catch (error) {
		logger.warn('hls.js failed to load:', error);
	}
	return video.mp4_url ? {kind: 'playing', src: video.mp4_url, Hls: null} : {kind: 'unplayable'};
}

interface FeedVideoProps {
	postId: string;
	video: FeedVideoResponse;
	// 'reel': fills the Reels stage instead of keeping the video's own shape in a card.
	variant?: 'card' | 'reel';
}

// Only the poster until the video plays, by itself (the feed's most visible video, FeedAutoplay) or with the play
// button: no video, playlist or hls.js request before that.
export const FeedVideo = observer(function FeedVideo({postId, video, variant = 'card'}: FeedVideoProps) {
	const {i18n} = useLingui();
	const autoplayId = useId();
	const active = FeedAutoplay.isActive(autoplayId);
	const [playback, setPlayback] = useState<Playback>({kind: 'poster'});
	const playbackRef = useRef(playback);
	playbackRef.current = playback;
	const [resumeAt, setResumeAt] = useState<number | null>(null);
	const boxRef = useRef<HTMLDivElement>(null);
	const posterReloaded = useRef(false);
	// Paused with a control: kept past the poster, so coming back into view does not start it again by itself.
	const pausedByViewer = useRef(false);
	useEffect(() => {
		const box = boxRef.current;
		if (!box) return;
		return FeedAutoplay.register(autoplayId, box);
	}, [autoplayId]);
	// A video left offscreen gives its source back and returns to the poster; playing again goes through start() (URLs).
	const backToPoster = useCallback((time: number | null) => {
		setResumeAt(time);
		setPlayback({kind: 'poster'});
	}, []);
	const fail = useCallback(() => setPlayback({kind: 'failed'}), []);
	const start = useCallback(
		async (byItself: boolean) => {
			if (playbackRef.current.kind !== 'poster') return;
			setPlayback({kind: 'starting'});
			// A page left open for a day: new URLs first (the reloaded post also replaces this one's props).
			const current = urlsRunOut(video) ? ((await FeedCommands.reloadPost(postId))?.video ?? video) : video;
			const next = await choosePlayback(current);
			// Scrolled on while it got ready: the poster stays and nothing is fetched.
			setPlayback(byItself && !FeedAutoplay.isActive(autoplayId) ? {kind: 'poster'} : next);
		},
		[autoplayId, postId, video],
	);
	useEffect(() => {
		if (active && playback.kind === 'poster' && !pausedByViewer.current) void start(true);
	}, [active, playback.kind, start]);
	// A video the viewer plays keeps playing (FeedAutoplay.claim) with the window unfocused or another one more in view.
	const viewerPlay = useCallback(() => {
		pausedByViewer.current = false;
		FeedAutoplay.claim(autoplayId);
	}, [autoplayId]);
	const viewerPause = useCallback(() => {
		pausedByViewer.current = true;
		FeedAutoplay.unclaim(autoplayId);
	}, [autoplayId]);
	// Pressing play asks for the video with its sound, and for the next ones.
	const play = () => {
		viewerPlay();
		FeedAutoplay.setMuted(false);
		void start(false);
	};
	// An expired poster URL answers 403. Once per video shown, and only near the end of the URLs, so a CDN failure does
	// not turn into a reload from every viewer.
	const reloadPoster = () => {
		if (posterReloaded.current || !urlsRunOut(video)) return;
		posterReloaded.current = true;
		void FeedCommands.reloadPost(postId);
	};
	const ratio = video.width && video.height ? `${video.width} / ${video.height}` : '16 / 9';
	return (
		<div
			ref={boxRef}
			className={clsx(styles.video, variant === 'reel' && styles.reel)}
			style={{'--feed-video-ratio': ratio} as React.CSSProperties}
			data-flx="feed.feed-video.video"
		>
			{(playback.kind === 'poster' || playback.kind === 'starting') && (
				<FocusRing offset={-2} data-flx="feed.feed-video.focus-ring">
					<button
						type="button"
						className={styles.poster}
						onClick={play}
						aria-label={i18n._(PLAY_VIDEO_DESCRIPTOR)}
						aria-busy={playback.kind === 'starting'}
						data-flx="feed.feed-video.poster.start"
					>
						<img
							src={video.poster_url}
							alt=""
							className={styles.posterImage}
							onError={reloadPoster}
							data-flx="feed.feed-video.poster-image"
						/>
						<span className={styles.play} aria-hidden="true" data-flx="feed.feed-video.play">
							<PlayIcon weight="fill" className={styles.playIcon} data-flx="feed.feed-video.play-icon" />
						</span>
						{video.duration != null && (
							<span className={styles.duration} data-flx="feed.feed-video.duration">
								{formatDuration(video.duration, i18n.locale)}
							</span>
						)}
					</button>
				</FocusRing>
			)}
			{playback.kind === 'playing' && (
				<FeedVideoPlayer
					src={playback.src}
					Hls={playback.Hls}
					poster={video.poster_url}
					duration={video.duration ?? undefined}
					resumeAt={resumeAt}
					loop={video.duration != null && video.duration <= LOOP_MAX_SECONDS}
					active={active}
					onRelease={backToPoster}
					onFailed={fail}
					onViewerPlay={viewerPlay}
					onViewerPause={viewerPause}
					data-flx="feed.feed-video.feed-video-player"
				/>
			)}
			{(playback.kind === 'unplayable' || playback.kind === 'failed') && (
				<p className={styles.status} role="status" data-flx="feed.feed-video.status">
					{i18n._(playback.kind === 'failed' ? PLAYBACK_FAILED_DESCRIPTOR : UNPLAYABLE_DESCRIPTOR)}
				</p>
			)}
		</div>
	);
});
